import {
  Box,
  ColorSwatch,
  Flex,
  Paper,
  Text,
  Title,
  Stack,
  Group,
} from "@mantine/core";
import { PieChart, Pie, Cell, PieLabel } from "recharts";
import { useMediaQuery } from "@mantine/hooks";
import {
  DocumentControlIcon,
  ManagingContentIcon,
  MarketingIcon,
  ProjectManagmentIcon,
} from "../../../shared/svg";
import useReportStore from "../../../store/useReportStore";
import { PDFPage } from "./PDFPage";

const COLORS = ["#05D8D2", "#A64DD7", "#6F40E2", "#3A8BD9"];
const RADIAN = Math.PI / 180;

const generateData = (
  keyNumbers: {
    title: string;
    reasoning: string;
    description: string;
  }[]
) => {
  return keyNumbers.map((item) => ({
    name: item.title,
    value: 100,
  }));
};

export const CareerInNumbersPDF = () => {
  const { report } = useReportStore();
  const matches = useMediaQuery("(max-width: 1100px)");

  const renderCustomizedLabel: PieLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    index,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const renderIcon = () => {
      switch (index) {
        case 0:
          return <MarketingIcon />;
        case 1:
          return <DocumentControlIcon />;
        case 2:
          return <ProjectManagmentIcon />;
        case 3:
          return <ManagingContentIcon />;
        default:
          return null;
      }
    };

    return (
      <g>
        <foreignObject
          x={x - (matches ? 22 : 38)}
          y={y - (matches ? 20 : 33.5)}
          width="76"
          height="67"
        >
          {renderIcon()}
        </foreignObject>
      </g>
    );
  };

  if (!report || !report.key_numbers || !report.key_numbers) {
    return null;
  }

  const data = generateData(report.key_numbers.data);

  return (
    <PDFPage padding>
      <Title size={48} mb={8}>
        {report.key_numbers.mark_down
          ? report.key_numbers?.mark_down.replace(/^## /, "")
          : "Career in Numbers"}
      </Title>
      <Text mb={80} size="28px" lh="42px" c="dimmed">
        {report.key_numbers?.explanation
          ? report.key_numbers?.explanation
          : "Description"}
      </Text>
      <Paper radius="15px" py="72px" px={34} mb={80} withBorder>
        <Flex
          justify="space-between"
          align="center"
          gap={32}
          direction={matches ? "column-reverse" : undefined}
        >
          <Group>
            <Stack gap={16}>
              <Text fw="bold" mb={8} size={matches ? "16px" : "28px"}>
                {report?.key_numbers?.overview
                  ? report?.key_numbers?.overview
                  : ""}
              </Text>
              {report.key_numbers.data.map((item, index) => (
                <Flex align="center" gap={8} key={index}>
                  <ColorSwatch
                    color={COLORS[index % COLORS.length]}
                    size={16}
                  />
                  <Group gap={4}>
                    <Text size={matches ? "16px" : "30px"}>{item.title}</Text>
                  </Group>
                </Flex>
              ))}
            </Stack>
          </Group>
          <Box>
            <PieChart width={matches ? 279 : 416} height={matches ? 279 : 416}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={matches ? 140 : 208}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
            </PieChart>
          </Box>
        </Flex>
      </Paper>

      <Paper radius="15px" p={matches ? 0 : 36} withBorder={!matches}>
        <Flex
          justify="space-between"
          align={matches ? "center" : "start"}
          ta="center"
          gap={16}
          direction={matches ? "column" : undefined}
        >
          {report.key_numbers.data.map((role, index) => (
            <Text
              key={index}
              maw={216}
              size={matches ? "16px" : "24px"}
              fw={matches ? "bold" : undefined}
            >
              <Title size={matches ? 36 : 64} c={COLORS[index % COLORS.length]}>
                {role.title.split(" ")[0]}
              </Title>
              {role.title.split(" ").slice(1).join(" ")}
            </Text>
          ))}
        </Flex>
      </Paper>
    </PDFPage>
  );
};
